import React, { useEffect, useState } from "react";
import Breadcrumb from "../components/Breadcrumb";
import Loading from "../components/loading";
import StorageService from "../services/StorageService";
import StockInfoService from "../services/stockInfoService";
import Select from "react-select";

import ReactPaginate from "react-paginate";
import usePermission from "../utils/UsePermission";
import ScreenData from "../config/ScreenData";
import UnAuthorized from "../views/Unathorized";


export default function ViewStockInfo() {
  const [allProjects, setAllProjects] = useState([]);
  const [allStores, setAllStores] = useState([]);
  const [allItemCategories, setAllItemCategories] = useState([]);
  const [allItems, setAllItems] = useState([]);

  // Filtered dropdown lists based on cascading selections
  const [filteredProjects, setFilteredProjects] = useState([]);
  const [filteredStores, setFilteredStores] = useState([]);
  const [filteredCategories, setFilteredCategories] = useState([]);
  const [filteredItems, setFilteredItems] = useState([]);

  const [selectedProjectId, setSelectedProjectId] = useState("");
  const [selectedStoreId, setSelectedStoreId] = useState("");
  const [selectedItemCategoryId, setSelectedItemCategoryId] = useState("");
  const [selectedItemId, setSelectedItemId] = useState("");

  const [tableData, setTableData] = useState([]);
  const [tableLoading, setTableLoading] = useState(false);

  const [currentPage, setCurrentPage] = useState(1);
  const [pageCount, setPageCount] = useState(0);
  const itemsPerPage = 10;

  const { permission } = usePermission(
    ScreenData.Stock_Info,
    () => { } // no showMessage needed here
  );

  const extractList = (res) => {
    if (Array.isArray(res)) return res;
    if (Array.isArray(res?.result)) return res.result;
    if (Array.isArray(res?.data)) return res.data;
    return [];
  };

  const loadDropdownData = async () => {
    const comp = StorageService.activeCompany();
    const cId = comp?.companyId || "all";
    const login = StorageService.loginData();
    const employeeId = login?.employeeId || null;
    const roleId = login?.roleId || null;

    try {
      const results = await Promise.allSettled([
        StockInfoService.getProjectsByCompany(cId, employeeId, roleId),
        StockInfoService.getStoresByCompany(cId, employeeId, roleId),
        StockInfoService.getItemCategoriesByCompany(cId, employeeId, roleId),
        StockInfoService.getItemsByCompany(cId, employeeId, roleId)
      ]);

      const projList = results[0].status === "fulfilled" ? extractList(results[0].value) : [];
      const storeList = results[1].status === "fulfilled" ? extractList(results[1].value) : [];
      const catList = results[2].status === "fulfilled" ? extractList(results[2].value) : [];
      const itemList = results[3].status === "fulfilled" ? extractList(results[3].value) : [];

      setAllProjects(projList);
      setFilteredProjects(projList);
      setAllStores(storeList);
      setFilteredStores(storeList);

      setAllItemCategories(catList);
      setFilteredCategories(catList);

      setAllItems(itemList);
      setFilteredItems(itemList);
    } catch (err) {
      console.error("Error loading dropdown options:", err);
    }
  };

  const loadTable = async () => {
    const comp = StorageService.activeCompany();
    const cId = comp?.companyId || null;
    const login = StorageService.loginData();
    const employeeId = login?.employeeId || null;
    const roleId = login?.roleId || null;

    try {
      setTableLoading(true);

      const res = await StockInfoService.getStockList({
        companyId: cId,
        projectId: selectedProjectId || null,
        storeId: selectedStoreId || null,
        itemCategoryId: selectedItemCategoryId || null,
        itemId: selectedItemId || null,
        employeeId,
        roleId
      });

      const fullData = extractList(res);

      const totalPages = Math.max(1, Math.ceil(fullData.length / itemsPerPage));
      setPageCount(totalPages);

      const safePage = Math.min(currentPage, totalPages);
      if (safePage !== currentPage) {
        setCurrentPage(safePage);
      }

      const start = (safePage - 1) * itemsPerPage;
      const end = start + itemsPerPage;

      setTableData(fullData.slice(start, end));
    } catch (err) {
      console.error("Table load error", err);
      setTableData([]);
    } finally {
      setTableLoading(false);
    }
  };

  useEffect(() => {
    loadDropdownData();
  }, []);

  useEffect(() => {
    setCurrentPage(1);
  }, [selectedProjectId, selectedStoreId, selectedItemCategoryId, selectedItemId]);

  useEffect(() => {
    loadTable();
  }, [selectedProjectId, selectedStoreId, selectedItemCategoryId, selectedItemId, currentPage]);

  // ----------------------------------------------------
  // Cascading & Backward Reset Handlers:
  // Item Category -> Item -> Store -> Project
  // ----------------------------------------------------

  // 1. Item Category change handler
  const handleCategoryChange = (opt) => {
    const catId = opt ? opt.value : "";
    setSelectedItemCategoryId(catId);

    // Reset downstream selections
    setSelectedItemId("");
    setSelectedStoreId("");
    setSelectedProjectId("");

    if (catId) {
      const matchedItems = allItems.filter(
        (i) => String(i.itemCategoryId || i.categoryId) === String(catId)
      );
      setFilteredItems(matchedItems);
    } else {
      setFilteredItems(allItems);
    }

    setFilteredStores(allStores);
    setFilteredProjects(allProjects);
  };

  // 2. Item change handler (Loads Stores for selected Item)
  const handleItemChange = async (opt) => {
    const itemId = opt ? opt.value : "";
    setSelectedItemId(itemId);

    // Reset downstream selections
    setSelectedStoreId("");
    setSelectedProjectId("");

    if (itemId) {
      try {
        const comp = StorageService.activeCompany();
        const res = await StockInfoService.getStoresByItem({
          companyId: comp?.companyId || null,
          itemId
        });
        const storeList = extractList(res);
        setFilteredStores(storeList);
      } catch (err) {
        console.error("Error fetching stores by item:", err);
        setFilteredStores(allStores);
      }
    } else {
      setFilteredStores(allStores);
    }

    setFilteredProjects(allProjects);
  };

  // 3. Store change handler (Loads Projects for selected Item & Store)
  const handleStoreChange = async (opt) => {
    const storeId = opt ? opt.value : "";
    setSelectedStoreId(storeId);

    // Reset downstream selection
    setSelectedProjectId("");

    if (storeId && selectedItemId) {
      try {
        const comp = StorageService.activeCompany();
        const res = await StockInfoService.getProjectsByItemAndStore({
          companyId: comp?.companyId || null,
          itemId: selectedItemId,
          storeId
        });
        const projList = extractList(res);
        setFilteredProjects(projList);
      } catch (err) {
        console.error("Error fetching projects by store:", err);
        setFilteredProjects(allProjects);
      }
    } else {
      setFilteredProjects(allProjects);
    }
  };

  // 4. Project change handler
  const handleProjectChange = (opt) => {
    const projId = opt ? opt.value : "";
    setSelectedProjectId(projId);
  };

  const formatProjectDisplay = (project) => {
    if (!project) return "-";
    let projObj = project;
    if (typeof project === "string" || typeof project === "number") {
      const match =
        allProjects.find(
          (p) =>
            String(p.id) === String(project) ||
            String(p.projectId) === String(project) ||
            p.projectName === project
        ) || {};
      projObj = Object.keys(match).length ? match : { projectName: String(project) };
    }

    const projectId = projObj.projectId || projObj.id;
    const matchedProject =
      (projectId &&
        allProjects.find(
          (p) => String(p.id) === String(projectId) || String(p.projectId) === String(projectId)
        )) ||
      (projObj.projectName &&
        allProjects.find((p) => p.projectName === projObj.projectName)) ||
      {};

    const projectName =
      projObj.projectName || projObj.name || matchedProject.projectName || "";
    const categoryName =
      projObj.projectCategoryName ||
      projObj.categoryName ||
      projObj.category ||
      matchedProject.projectCategoryName ||
      matchedProject.categoryName ||
      matchedProject.category ||
      "";
    const projectCode =
      projObj.projectCode || matchedProject.projectCode || "";
    const categoryInitial = String(categoryName).trim().charAt(0).toUpperCase();

    if (projectName) {
      if (categoryInitial) {
        return `${categoryInitial}*${projectName}`;
      }
      if (projectCode) {
        const codeInitial = String(projectCode).trim().charAt(0).toUpperCase();
        return `${codeInitial}*${projectName}`;
      }
      return projectName;
    }
    if (projectCode) {
      const codeInitial = String(projectCode).trim().charAt(0).toUpperCase();
      return `${codeInitial}*`;
    }
    return categoryInitial ? `${categoryInitial}*` : "-";
  };

  // Options for react-select
  const projectOptions = filteredProjects.map((p) => {
    const id = p.id || p.projectId;
    return { value: String(id), label: formatProjectDisplay(p) };
  });

  const storeOptions = filteredStores.map((s) => {
    const id = s.id || s.storeId;
    return { value: String(id), label: s.storeName };
  });

  const categoryOptions = filteredCategories.map((c) => {
    const id = c.id || c.itemCategoryId;
    return { value: String(id), label: c.categoryName || c.itemCategoryName };
  });

  const itemOptions = filteredItems.map((i) => {
    const id = i.id || i.itemId;
    const label = i.itemCode
      ? `${i.itemCode}${i.itemDescription ? ` - ${i.itemDescription}` : ""}`
      : (i.itemName || i.itemDescription);
    return { value: String(id), label };
  });

  if (permission === null) {
    return <div className="text-center mt-5">Loading...</div>;
  }

  if (!permission || permission.canView !== 1) {
    return <UnAuthorized />;
  }

  return (
    <>
      <Breadcrumb pageTitle="Stock Available" secondTitle="Inventory" />

      <div className="card p-4 bg-white shadow-sm">
        <div className="row mb-3">

          {/* 1. ITEM CATEGORY */}
          <div className="col-md-3">
            <label className="form-label">Item Category</label>
            <Select
              options={categoryOptions}
              value={categoryOptions.find((opt) => opt.value === String(selectedItemCategoryId)) || null}
              onChange={handleCategoryChange}
              isSearchable={true}
              isClearable={true}
              placeholder="Select Item Category"
              menuPortalTarget={document.body}
              styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
            />
          </div>

          {/* 2. ITEM */}
          <div className="col-md-3">
            <label className="form-label">Item</label>
            <Select
              options={itemOptions}
              value={itemOptions.find((opt) => opt.value === String(selectedItemId)) || null}
              onChange={handleItemChange}
              isDisabled={!selectedItemCategoryId}
              isSearchable={true}
              isClearable={true}
              placeholder={!selectedItemCategoryId ? "Select category first" : "Select Item"}
              menuPortalTarget={document.body}
              styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
            />
          </div>

          {/* 3. STORE */}
          <div className="col-md-3">
            <label className="form-label">Store</label>
            <Select
              options={storeOptions}
              value={storeOptions.find((opt) => opt.value === String(selectedStoreId)) || null}
              onChange={handleStoreChange}
              isDisabled={!selectedItemId}
              isSearchable={true}
              isClearable={true}
              placeholder={!selectedItemId ? "Select item first" : "Select Store"}
              menuPortalTarget={document.body}
              styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
            />
          </div>

          {/* 4. PROJECT */}
          <div className="col-md-3">
            <label className="form-label">Project</label>
            <Select
              options={projectOptions}
              value={projectOptions.find((opt) => opt.value === String(selectedProjectId)) || null}
              onChange={handleProjectChange}
              isDisabled={!selectedStoreId}
              isSearchable={true}
              isClearable={true}
              placeholder={!selectedStoreId ? "Select store first" : "Select Project"}
              menuPortalTarget={document.body}
              styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
            />
          </div>

        </div>

        <div className="table-responsive w-100 mt-3">
          <table className="table table-bordered mb-0">
            <thead className="table-light">
              <tr>
                <th>Item Category</th>
                <th>Item Code</th>
                <th>Description</th>
                <th>Project</th>
                <th>Store</th>
                <th>UOM</th>
                <th>Quantity</th>
              </tr>
            </thead>

            <tbody>
              {tableLoading ? (
                <tr>
                  <td colSpan="7" className="text-center py-4">
                    <Loading />
                  </td>
                </tr>
              ) : tableData.length === 0 ? (
                <tr>
                  <td colSpan="7" className="text-center">
                    No stock available
                  </td>
                </tr>
              ) : (
                tableData.map((row, index) => (
                  <tr key={index}>
                    <td>{row.itemCategoryName || row.categoryName || "-"}</td>
                    <td>{row.itemCode}</td>
                    <td>{row.itemDescription}</td>
                    <td>{formatProjectDisplay(row)}</td>
                    <td>{row.storeName}</td>
                    <td>{row.uom}</td>
                    <td>{row.quantity}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        <ReactPaginate
          forcePage={Math.max(0, Math.min(currentPage - 1, Math.max(pageCount - 1, 0)))}
          onPageChange={(e) => setCurrentPage(e.selected + 1)}
          pageCount={Math.max(1, pageCount)}
          pageRangeDisplayed={3}
          marginPagesDisplayed={2}
          previousLabel="< Previous"
          nextLabel="Next >"
          containerClassName="pagination-container"
          pageClassName="page-item"
          pageLinkClassName="page-link"
          previousClassName="page-item"
          nextClassName="page-item"
          activeClassName="active"
        />
      </div>
    </>
  );
}
